import * as THREE from 'three';
import { floorplannerStore } from '../../store/floorplannerStore';
import { renderStore } from '../../store/renderStore';
import { editorStore } from '../../store/editorStore';
import { PersistedCorner, WallConnectionEnd, WallConnectionStart, WallCornerKey, WallCornerPersistedMap, WallShapeType, WallType } from '../../types/floorplannerTypes';
import { calculateLineVertices } from './calculateLineVertices';
import { populateDefaultCorners, setCornerPoint, RECT_CORNER_ORDER } from '../../utils/wallCornerHelpers';
import { assertValidWallGeometry, canValidateRectangularWall } from '../../utils/wallGeometryValidation';
import { getEndpointImaginaryIntersections } from '../../utils/imaginaryLineHelpers';
import { getSymbolInsertGeometry, SymbolInsertGeometry } from '../../utils/symbolAttachmentGeometry';
import { getMultiJointEndpointTrimPair } from '../../utils/multiJointEndpointTrim';

const CORNER_EPSILON = 1e-6;

/**
 * Creates partial endcap vertices using intersection logic.
 * This calculates where the current wall's outline intersects with the connected wall's outline.
 *
 * @param wall The current wall
 * @param connectedWall The connected wall
 * @param endpoint Which endpoint of the current wall ('start' or 'end')
 * @param wallWidth The current wall's width
 * @param lineWeight The outline line weight
 * @returns Array of vertices for the partial endcap, or empty array if not needed
 */
const createPartialEndcapVertices = (
    wall: WallType,
    connectedWall: WallType,
    endpoint: 'start' | 'end',
    wallWidth: number,
    lineWeight: number
): THREE.Vector2[] => {
    // Use intersection logic to find where OUTLINE edges meet
    const intersections = getEndpointImaginaryIntersections(
        wall,
        endpoint,
        connectedWall,
        wallWidth,
        {
            forceSharedEndpoint: true,
            includeLineWeight: true,
            preferClosestPerSourceSide: true
        }
    );

    if (!intersections) {
        return [];
    }

    // Get the connection point
    const connectionPoint = endpoint === 'start' ? wall.start : wall.end;

    // Calculate the wall's outline edges at the connection point
    const direction = new THREE.Vector2().subVectors(wall.end, wall.start);
    const length = direction.length();
    if (length < CORNER_EPSILON) {
        return [];
    }
    direction.normalize();
    const perpendicular = new THREE.Vector2(-direction.y, direction.x);
    const halfWidth = wallWidth / 2;

    // Outline corners at the connection point (outer edges of the current wall)
    const leftOutlineCorner = connectionPoint.clone().add(perpendicular.clone().multiplyScalar(halfWidth));
    const rightOutlineCorner = connectionPoint.clone().sub(perpendicular.clone().multiplyScalar(halfWidth));

    // Create two partial endcap rectangles (one on each side)
    // Each rectangle goes from the current wall's outline corner to the intersection point
    // We return 8 vertices (like other endcaps) where indices [0,1] and [4,5] form the two bridges
    const result = [
        // Left side bridge: from wall's left outline corner to intersection
        leftOutlineCorner.clone(),
        intersections.left.clone(),
        // Fillers
        intersections.left.clone(),
        leftOutlineCorner.clone(),
        // Right side bridge: from wall's right outline corner to intersection
        rightOutlineCorner.clone(),
        intersections.right.clone(),
        // Fillers
        intersections.right.clone(),
        rightOutlineCorner.clone(),
    ];

    return result;
};

/**
 * Determines if a wall needs a partial endcap when connected to another wall.
 * A partial endcap is needed when the current wall is wider than the connected wall
 * and they connect at the same angle (180 degrees).
 *
 * @param wall The wall to check
 * @param connectionPosition The position of the connection (start or end)
 * @returns Object with needsPartialEndcap boolean, connectedWallWidth number, and connectedWall reference
 */
const needsPartialEndcap = (wall: WallType, connectionPosition: number): { needsPartialEndcap: boolean; connectedWallWidth: number; connectedWall: WallType | null } => {
    // Check edge connections
    if (wall.edgeConnections) {
        // Check for edge connections at the specified position
        const hasConnection = wall.edgeConnections.some(conn => {
            return (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'start' && connectionPosition === WallConnectionStart) ||
                   (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'end' && connectionPosition === WallConnectionEnd) ||
                   (conn.targetWallId === wall.id && conn.targetEndpoint === 'start' && connectionPosition === WallConnectionStart) ||
                   (conn.targetWallId === wall.id && conn.targetEndpoint === 'end' && connectionPosition === WallConnectionEnd);
        });

        if (!hasConnection) {
            return { needsPartialEndcap: false, connectedWallWidth: 0, connectedWall: null };
        }

        // Get the connected wall ID
        const connection = wall.edgeConnections.find(conn => {
            return (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'start' && connectionPosition === WallConnectionStart) ||
                   (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'end' && connectionPosition === WallConnectionEnd) ||
                   (conn.targetWallId === wall.id && conn.targetEndpoint === 'start' && connectionPosition === WallConnectionStart) ||
                   (conn.targetWallId === wall.id && conn.targetEndpoint === 'end' && connectionPosition === WallConnectionEnd);
        });

        if (!connection) {
            return { needsPartialEndcap: false, connectedWallWidth: 0, connectedWall: null };
        }

        // Get the connected wall
        const otherWallId = connection.sourceWallId === wall.id ? connection.targetWallId : connection.sourceWallId;
        const connectedWall = floorplannerStore.wallsMap.get(otherWallId);
        if (!connectedWall) {
            return { needsPartialEndcap: false, connectedWallWidth: 0, connectedWall: null };
        }

        const currentWallWidth = wall.wallWidth || floorplannerStore.wallWidth;
        const connectedWallWidth = connectedWall.wallWidth || floorplannerStore.wallWidth;

        // Check if walls are at the same angle (180 degrees)
        const angle = Math.abs(floorplannerStore.angleBetweenTwoWalls(wall, connectedWall));
        const EPS = 0.01;
        // Treat both 0° (same direction) and 180° (opposite) as colinear
        const isSameAngle = angle < EPS || Math.abs(angle - Math.PI) < EPS;

        // Need partial endcap if current wall is wider and they're at same angle
        const needsPartial = isSameAngle && currentWallWidth > connectedWallWidth;

        return { needsPartialEndcap: needsPartial, connectedWallWidth, connectedWall };
    }

    // Legacy mode: check wall.centerConnections
    const connection = wall.centerConnections?.find(c => c.sourcePosition === connectionPosition);
    if (!connection) {
        return { needsPartialEndcap: false, connectedWallWidth: 0, connectedWall: null };
    }

    const connectedWall = floorplannerStore.wallsMap.get(connection.id);
    if (!connectedWall) {
        return { needsPartialEndcap: false, connectedWallWidth: 0, connectedWall: null };
    }

    const currentWallWidth = wall.wallWidth || floorplannerStore.wallWidth;
    const connectedWallWidth = connectedWall.wallWidth || floorplannerStore.wallWidth;

    // Check if walls are at the same angle (180 degrees)
    const angle = Math.abs(floorplannerStore.angleBetweenTwoWalls(wall, connectedWall));
    const EPS = 0.01;
    // Treat both 0° (same direction) and 180° (opposite) as colinear
    const isSameAngle = angle < EPS || Math.abs(angle - Math.PI) < EPS;

    // Need partial endcap if current wall is wider and they're at same angle
    const needsPartial = isSameAngle && currentWallWidth > connectedWallWidth;

    return { needsPartialEndcap: needsPartial, connectedWallWidth, connectedWall };
};

const toVector2 = (point: THREE.Vector2 | { x: number; y: number }): THREE.Vector2 =>
    point instanceof THREE.Vector2 ? point.clone() : new THREE.Vector2(point.x, point.y);

const getChordDirection = (wall: WallType): THREE.Vector2 => {
    const direction = new THREE.Vector2().subVectors(toVector2(wall.end), toVector2(wall.start));
    const length = direction.length();
    return length > CORNER_EPSILON ? direction.divideScalar(length) : new THREE.Vector2(1, 0);
};

const areStraightWallsCollinear = (wallA: WallType, wallB: WallType): boolean => {
    if (wallA.lineForm === "arc" || wallB.lineForm === "arc") {
        return false;
    }

    const directionA = getChordDirection(wallA);
    const directionB = getChordDirection(wallB);
    const cross = directionA.x * directionB.y - directionA.y * directionB.x;
    return Math.abs(cross) < 0.05;
};

const isPointCoveredByWallFootprint = (point: THREE.Vector2, wall: WallType): boolean => {
    const start = toVector2(wall.start);
    const end = toVector2(wall.end);
    const direction = new THREE.Vector2().subVectors(end, start);
    const length = direction.length();
    if (length < CORNER_EPSILON) {
        return false;
    }

    direction.divideScalar(length);
    const perpendicular = new THREE.Vector2(-direction.y, direction.x);
    const relative = new THREE.Vector2().subVectors(point, start);
    const along = relative.dot(direction);
    const off = Math.abs(relative.dot(perpendicular));
    const halfWidth = (wall.wallWidth ?? floorplannerStore.wallWidth) / 2;
    const tolerance = 1e-3;
    return along >= -tolerance && along <= length + tolerance && off <= halfWidth + tolerance;
};

const hasCollinearMiddleCoverAtEndpoint = (wall: WallType, endpoint: "start" | "end"): boolean => {
    if (renderStore.isDragging) {
        return false;
    }

    const endpointPosition = toVector2(endpoint === "start" ? wall.start : wall.end);
    for (const otherWall of floorplannerStore.wallsMap.values()) {
        if (otherWall.id === wall.id || !areStraightWallsCollinear(wall, otherWall)) {
            continue;
        }

        const coversEndpoint = (otherWall.edgeConnections ?? []).some(connection =>
            connection.targetWallId === wall.id &&
            connection.targetEndpoint === "middle" &&
            (connection.sourceEndpoint === "start" || connection.sourceEndpoint === "end") &&
            isPointCoveredByWallFootprint(endpointPosition, otherWall)
        );

        if (coversEndpoint) {
            return true;
        }
    }

    return false;
};

const applyPersistedCornersToShape = (wall: WallType, shape: WallShapeType, overrideCornerState?: WallCornerPersistedMap) => {
    if (wall.lineForm === "arc") {
        return;
    }

    // CRITICAL: Always fetch fresh wall from store to ensure we have current connection state
    // This prevents stale corner state from being applied after disconnections
    const freshWall = floorplannerStore.wallsMap.get(wall.id);
    const wallToUse = freshWall ?? wall;

    // Get corner state based on current connections - ignore any override that might be stale
    const state = floorplannerStore.getCornerStateForApplication(wallToUse);
    if (!state) {
        return;
    }

    const cornerState = state;
    if (!cornerState) {
        return;
    }

    // Use fresh wall for geometry calculations to ensure consistency
    const direction = wallToUse.end.clone().sub(wallToUse.start);
    const length = direction.length();
    if (length <= CORNER_EPSILON) {
        return;
    }

    const dirNorm = direction.clone().normalize();
    const perpendicular = new THREE.Vector2(-dirNorm.y, dirNorm.x);

    const toWorldPoint = (corner?: PersistedCorner): THREE.Vector2 | undefined => {
        if (!corner) {
            return undefined;
        }
        if (typeof corner.s === "number" && typeof corner.t === "number") {
            // Use fresh wall's start position for coordinate transformation
            return wallToUse.start.clone()
                .add(dirNorm.clone().multiplyScalar(corner.s))
                .add(perpendicular.clone().multiplyScalar(corner.t));
        }
        if (typeof corner.x === "number" && typeof corner.y === "number") {
            return new THREE.Vector2(corner.x, corner.y);
        }
        return undefined;
    };

    // Filter to only process valid corner keys
    const validKeys = new Set(RECT_CORNER_ORDER);
    Object.entries(cornerState)
        .filter(([key]) => validKeys.has(key as WallCornerKey))
        .forEach(([key, value]) => {
            const point = toWorldPoint(value);
            if (point) {
                setCornerPoint(shape, key as WallCornerKey, point);
            }
        });
};

const applyMultiJointEndpointTrimToFill = (wall: WallType, shape: WallShapeType) => {
    if (wall.lineForm === "arc") {
        return;
    }

    const trimEndpoint = (endpoint: "start" | "end") => {
        const trimPair = getMultiJointEndpointTrimPair(wall, endpoint);
        if (!trimPair) {
            return;
        }

        if (endpoint === "start") {
            setCornerPoint(shape, "topLeft", trimPair.left);
            setCornerPoint(shape, "topRight", trimPair.right);
        } else {
            setCornerPoint(shape, "bottomLeft", trimPair.left);
            setCornerPoint(shape, "bottomRight", trimPair.right);
        }
    };

    trimEndpoint("start");
    trimEndpoint("end");
};

const deriveTrimmedEndpointsFromState = (
    wall: WallType,
    cornerState?: WallCornerPersistedMap,
): { start: THREE.Vector2; end: THREE.Vector2 } | undefined => {
    if (!cornerState) {
        return undefined;
    }

    const startCorner = cornerState.topLeft ?? cornerState.bottomLeft;
    const endCorner = cornerState.topRight ?? cornerState.bottomRight;

    if (
        !startCorner ||
        !endCorner ||
        typeof startCorner.s !== "number" ||
        typeof endCorner.s !== "number"
    ) {
        return undefined;
    }

    const direction = wall.end.clone().sub(wall.start);
    const length = direction.length();
    if (length <= CORNER_EPSILON) {
        return undefined;
    }

    const dirNorm = direction.clone().normalize();
    const startPoint = wall.start.clone().add(dirNorm.clone().multiplyScalar(startCorner.s));
    const endPoint = wall.start.clone().add(dirNorm.clone().multiplyScalar(endCorner.s));

    return {
        start: startPoint,
        end: endPoint,
    };
};

/**
 * Wall Shape Composition - Base Geometry Generation
 *
 * PURPOSE:
 * This file generates the base 4-point geometry for walls (fill, outlines, endcaps).
 * It is the first step in the wall rendering pipeline.
 *
 * ARCHITECTURAL ROLE:
 * - Generates base wall shapes from wall start/end positions
 * - Creates fill shapes (inner rectangles)
 * - Creates outline shapes (left/right edges)
 * - Creates endcap shapes (start/end caps)
 * - Applies persisted corner state (for legacy compatibility)
 *
 * CURRENT ARCHITECTURE:
 * - All straight walls are 4-point rectangles (no 5-6 point polygons)
 * - Joint polygons (from jointPolygonGenerator.ts) fill gaps at connections
 * - Outline segmentation (from outlineSegmentation.ts) handles outline gaps
 * - Walls remain simple rectangles, with separate polygons filling gaps
 *
 * @see jointPolygonGenerator.ts - Gap-filling polygons
 * @see outlineSegmentation.ts - Segmented outlines
 * @see renderStore.ts - Orchestrates rendering pipeline
 * @see EDGE_CONNECTION_ARCHITECTURE.md - Architecture documentation
 */
export const composeLines = (
    filledLines: WallShapeType[],
    leftLines: WallShapeType[],
    rightLines: WallShapeType[],
    endCaps: WallShapeType[],
) => {
    const {
        wallsMap,
        wallWidth,
    } = floorplannerStore;

    // Set reconstruction flag to prevent flashing during batch composition
    // This tells the renderer we're doing bulk updates, so don't redraw until complete
    renderStore.setIsReconstructingShapes(true);
    // Block redraws during reconstruction to prevent intermediate states from showing
    renderStore.setSafeToRedraw(false);

    try {
      // Iterate through all walls in the floorplan
      // Using Array.from() to convert ObservableMap to array for iteration
      Array.from(wallsMap.values()).forEach((wall) => {
        // Get wall's custom properties or use defaults
        const customWallWidth = wall.wallWidth || wallWidth;
        const customWallWeight = floorplannerStore.convertLineWeightToWorld(wall.lineWeight || floorplannerStore.wallLineWeight)

        // CREATE FILLED SHAPE (inner wall)
        // This is the main wall body - always a 4-point rectangle in edge-based mode
        const filledShape: WallShapeType = {
            wall: wall,
            paths: calculateLineVertices(
                wall.start,
                wall.end,
                0,              // No offset for inner shape
                customWallWeight,
                customWallWidth,
                wall.lineForm === "arc" ? "arc" : "straight",
                wall.controlPoint
            ),
            lineSide: "inner",
        };

        // Populate default corner positions (topLeft, topRight, bottomRight, bottomLeft)
        // This creates the basic 4-point rectangle
        populateDefaultCorners(filledShape);

        // CRITICAL: Get fresh wall from store to check current connection state
        // This ensures we don't apply stale corner state after disconnections
        const freshWallForCorners = floorplannerStore.wallsMap.get(wall.id) ?? wall;
        
        // Check if wall has endpoint connections (determines if we should apply corner state)
        const hasStartConnectionBatch = (freshWallForCorners.centerConnections?.some(c => c.sourcePosition === WallConnectionStart) ?? false) ||
          (freshWallForCorners.edgeConnections?.some(c => 
            (c.sourceWallId === freshWallForCorners.id && c.sourceEndpoint === "start") ||
            (c.targetWallId === freshWallForCorners.id && c.targetEndpoint === "start")
          ) ?? false);
        const hasEndConnectionBatch = (freshWallForCorners.centerConnections?.some(c => c.sourcePosition === WallConnectionEnd) ?? false) ||
          (freshWallForCorners.edgeConnections?.some(c => 
            (c.sourceWallId === freshWallForCorners.id && c.sourceEndpoint === "end") ||
            (c.targetWallId === freshWallForCorners.id && c.targetEndpoint === "end")
          ) ?? false);

        // Only apply persisted corner state if there are connections that would require it
        // This prevents stale trimmed corners from being applied after disconnection
        if (hasStartConnectionBatch || hasEndConnectionBatch) {
          // Apply any persisted corner state from previous operations
          // In legacy mode, this would include modified corners from multi-joint rendering
          // In edge-based mode, corners remain at default positions
          // Note: applyPersistedCornersToShape now fetches fresh wall state internally
          applyPersistedCornersToShape(wall, filledShape);
        } else {
          // CRITICAL: If there are no connections, ensure corners are at default positions
          // This resets any stale trimmed corners that might have been applied
          populateDefaultCorners(filledShape);
        }

        applyMultiJointEndpointTrimToFill(wall, filledShape);

        // Update the stored corner state for this wall
        // This maintains state for potential future modifications
        floorplannerStore.updateWallCornerStateFromShape(filledShape);

        // Validate wall geometry if it's a simple rectangle
        // Skips validation for complex shapes (arc walls, very short walls, etc.)
        if (canValidateRectangularWall(filledShape)) {
            assertValidWallGeometry(filledShape, "composeLines", {
                defaultWallWidth: wallWidth,
            });
        }

        // Get fresh corner state for trimmed endpoints calculation
        const freshWall = floorplannerStore.wallsMap.get(wall.id) ?? wall;
        const persistedCorners = floorplannerStore.getCornerStateForApplication(freshWall);

        // Calculate trimmed endpoints if wall has been modified
        // In edge-based mode, this is typically undefined (walls aren't trimmed)
        // In legacy mode, this would contain modified endpoints from multi-joint rendering
        const trimmedEndpoints = wall.lineForm === "arc" ? undefined : deriveTrimmedEndpointsFromState(wall, persistedCorners);

        // Use trimmed endpoints if available, otherwise use original wall endpoints
        // In edge-based mode, this is usually just wall.start and wall.end
        const outlineStart = trimmedEndpoints?.start ?? wall.start;
        const outlineEnd = trimmedEndpoints?.end ?? wall.end;

        // Add the filled shape to the output array
        // This will be rendered as the wall's main body
        filledLines.push(filledShape);

        // CREATE LEFT OUTLINE SHAPE
        // This is the left edge of the wall - offset by width/2
        const leftShape: WallShapeType = {
            wall: wall,
            paths: calculateLineVertices(
                outlineStart,
                outlineEnd,
                -(customWallWidth / 2),  // Negative offset = left side (at edge of fill)
                customWallWeight,
                customWallWidth,
                wall.lineForm === "arc" ? "arcLeft" : "left",
                wall.controlPoint
            ),
            lineSide: "left",
        };
        // Populate corners for the left outline
        populateDefaultCorners(leftShape);
        // Add to left lines array
        leftLines.push(leftShape);

        // CREATE RIGHT OUTLINE SHAPE
        // This is the right edge of the wall - offset by width/2
        const rightShape: WallShapeType = {
            wall: wall,
            paths: calculateLineVertices(
                outlineStart,
                outlineEnd,
                customWallWidth / 2,  // Positive offset = right side (at edge of fill)
                customWallWeight,
                customWallWidth,
                wall.lineForm === "arc" ? "arcRight" : "right",
                wall.controlPoint
            ),
            lineSide: "right",
        };
        // Populate corners for the right outline
        populateDefaultCorners(rightShape);
        // Add to right lines array
        rightLines.push(rightShape);

        // FILTER ENDPOINT CONNECTIONS
        // Get only connections at the very start or end of the wall (not midpoints)
        // Mid-point connections are handled differently

        // Check if this wall has connections at its endpoints
        let hasStartConnection = false;
        let hasEndConnection = false;

        if (wall.edgeConnections) {
            // Check edge connections
            hasStartConnection = wall.edgeConnections.some(conn => {
                return (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'start') ||
                       (conn.targetWallId === wall.id && conn.targetEndpoint === 'start');
            });
            hasEndConnection = wall.edgeConnections.some(conn => {
                return (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'end') ||
                       (conn.targetWallId === wall.id && conn.targetEndpoint === 'end');
            });
        } else {
            // Legacy mode: check wall.centerConnections
            const endPoints = wall.centerConnections?.filter(
                (c) => c.sourcePosition === WallConnectionEnd || c.sourcePosition === WallConnectionStart,
            ) || [];
            hasStartConnection = endPoints.some(c => c.sourcePosition === WallConnectionStart);
            hasEndConnection = endPoints.some(c => c.sourcePosition === WallConnectionEnd);
        }

        hasStartConnection = hasStartConnection || hasCollinearMiddleCoverAtEndpoint(wall, "start");
        hasEndConnection = hasEndConnection || hasCollinearMiddleCoverAtEndpoint(wall, "end");

        // Check if walls need partial endcaps (for width differences)
        // A partial endcap is needed when a wider wall connects to a narrower wall
        const startPartial = needsPartialEndcap(wall, WallConnectionStart);
        const endPartial = needsPartialEndcap(wall, WallConnectionEnd);

        // DETERMINE ENDCAP TYPE AND CREATE SHAPES

        // Case 1: No connections at either end
        // Draw a full endcap that covers both start and end
        if (!hasStartConnection && !hasEndConnection) {
            const endCapShape: WallShapeType = {
                wall: wall,
                paths: calculateLineVertices(
                    outlineStart, outlineEnd, 0, customWallWeight, customWallWidth, "endCapBoth"
                ),
                lineSide: "endcap",
            };
            populateDefaultCorners(endCapShape);
            endCaps.push(endCapShape);
        } else {
            // Case 2: Has connections - draw selective endcaps

            // Draw start cap only if there's no connection at the start
            // Free ends (not connected) get regular caps
            if (!hasStartConnection) {
                const startCapShape: WallShapeType = {
                    wall: wall,
                    paths: calculateLineVertices(
                        outlineStart, outlineEnd, 0, customWallWeight, customWallWidth, "endCapStart"
                    ),
                    lineSide: "endcap",
                };
                populateDefaultCorners(startCapShape);
                endCaps.push(startCapShape);
            }

            // Draw end cap only if there's no connection at the end
            if (!hasEndConnection) {
                const endCapShape: WallShapeType = {
                    wall: wall,
                    paths: calculateLineVertices(
                        outlineStart, outlineEnd, 0, customWallWeight, customWallWidth, "endCapEnd"
                    ),
                    lineSide: "endcap",
                };
                populateDefaultCorners(endCapShape);
                endCaps.push(endCapShape);
            }

            // Draw partial endcaps for connected ends when walls have different widths
            // This fills the gap when a wider wall connects to a narrower wall
            // Use intersection logic to properly close the gap at any angle
            if (hasStartConnection && startPartial.needsPartialEndcap && startPartial.connectedWall) {
                const partialStartVertices = createPartialEndcapVertices(
                    wall,
                    startPartial.connectedWall,
                    'start',
                    customWallWidth,
                    customWallWeight
                );
                if (partialStartVertices.length > 0) {
                    const partialStartShape: WallShapeType = {
                        wall: wall,
                        paths: partialStartVertices,
                        lineSide: "endcap",
                    };
                    populateDefaultCorners(partialStartShape);
                    endCaps.push(partialStartShape);
                }
            }

            // Partial endcap for the end connection
            if (hasEndConnection && endPartial.needsPartialEndcap && endPartial.connectedWall) {
                const partialEndVertices = createPartialEndcapVertices(
                    wall,
                    endPartial.connectedWall,
                    'end',
                    customWallWidth,
                    customWallWeight
                );
                if (partialEndVertices.length > 0) {
                    const partialEndShape: WallShapeType = {
                        wall: wall,
                        paths: partialEndVertices,
                        lineSide: "endcap",
                    };
                    populateDefaultCorners(partialEndShape);
                    endCaps.push(partialEndShape);
                }
            }
        }
      });
    } catch (error) {
      // If an error occurs, log it and re-throw
      // The finally block will still execute to clean up
      console.error('Error in composeLines:', error);
      throw error;
    } finally {
      // ALWAYS clear reconstruction flag after batch composition is complete
      // This is in a finally block to ensure it runs even if an error occurs
      renderStore.setIsReconstructingShapes(false);

      // Only re-enable redraws if we're not in a deferred update batch
      // Deferred batches (from beginDeferredBatch/endDeferredBatch) handle their own redraw
      if (renderStore.deferredUpdateDepth === 0) {
        renderStore.setSafeToRedraw(true);
      }
    }
};

/**
 * Compose Paths - Single Wall Geometry Generation
 *
 * PURPOSE:
 * This function generates the base 4-point geometry for a single wall.
 * It's similar to composeLines() but operates on one wall at a time instead of all walls.
 *
 * USAGE:
 * - Used when only one wall needs to be re-rendered (e.g., during drag)
 * - More efficient than recomposing all walls
 * - Called from WallShape.tsx component
 *
 * DIFFERENCE FROM composeLines():
 * - composeLines(): Processes all walls in batch (used during initial render)
 * - composePaths(): Processes single wall (used during updates)
 *
 * EDGE-BASED MODE:
 * - All straight walls are 4-point rectangles
 * - No geometry modification at joints
 * - Joint polygons handle gap filling
 * - Outline segmentation handles outline gaps
 *
 * LEGACY MODE:
 * - May modify wall geometry based on connections
 * - Corner state tracking
 * - MultiJointRenderer may modify shapes
 *
 * @see composeLines() - Batch composition of all walls
 */
export const composePaths = (
    wallId: string,
    innerShapes: WallShapeType[],
    outerShapes1: WallShapeType[],
    outerShapes2: WallShapeType[],
    endCaps: WallShapeType[],
) => {
    // Get the specific wall to compose
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall) {
        // Wall not found - nothing to do
        return;
    }

    // Set reconstruction flag to prevent flashing during path composition
    // This prevents intermediate states from showing during update
    renderStore.setIsReconstructingShapes(true);
    // Block redraws during reconstruction
    renderStore.setSafeToRedraw(false);

    try {
      const {
          wallWidth,
      } = floorplannerStore;

    // Get wall's custom properties or use defaults
    const customWallWidth = wall.wallWidth || wallWidth;
    const customWallWeight = floorplannerStore.convertLineWeightToWorld(wall.lineWeight || floorplannerStore.wallLineWeight)

    // Precompute straight-insert geometries for arc walls with symbol attachments.
    // Each insert represents a flat section on the curve where a door/window/void sits.
    let arcInserts: SymbolInsertGeometry[] | undefined;
    if (wall.lineForm === "arc" && wall.controlPoint && wall.symbolAttachments?.length) {
      arcInserts = [];
      for (const att of wall.symbolAttachments) {
        const sym = floorplannerStore.symbolsMap.get(att.symbolId);
        if (!sym) continue;
        arcInserts.push(getSymbolInsertGeometry(wall, sym, att.positionFromStart, floorplannerStore.doorFrameWidth));
      }
      if (arcInserts.length === 0) arcInserts = undefined;
    }

    // CREATE INNER SHAPE (wall fill)
    // This is the main body of the wall
    const innerShape: WallShapeType = {
        wall: { ...wall },
        paths: calculateLineVertices(
            wall.start,
            wall.end,
            0,  // No offset for inner shape
            customWallWeight,
            customWallWidth,
            wall.lineForm === "arc" ? "arc" : "straight",
            wall.controlPoint,
            undefined,
            arcInserts,
        ),
        lineSide: "inner",
    };

    // Populate default corners (4-point rectangle)
    populateDefaultCorners(innerShape);

    // CRITICAL: Get fresh wall from store to check current connection state
    // This ensures we don't apply stale corner state after disconnections
    const freshWallForCorners = floorplannerStore.wallsMap.get(wallId) ?? wall;
    
    // Edge-based joints render from their edge metadata; persisted corner trimming
    // is only valid for legacy center-line connections.
    const hasStartConnection = freshWallForCorners.centerConnections?.some(c => c.mode !== "edge" && c.sourcePosition === WallConnectionStart) ?? false;
    const hasEndConnection = freshWallForCorners.centerConnections?.some(c => c.mode !== "edge" && c.sourcePosition === WallConnectionEnd) ?? false;

    // Only apply persisted corner state if there are connections that would require it
    // This prevents stale trimmed corners from being applied after disconnection
    if (hasStartConnection || hasEndConnection) {
      // Apply persisted corner state (for legacy compatibility)
      // Note: applyPersistedCornersToShape now fetches fresh wall state internally
      applyPersistedCornersToShape(wall, innerShape);
    } else {
      // CRITICAL: If there are no connections, ensure corners are at default positions
      // This resets any stale trimmed corners that might have been applied
      populateDefaultCorners(innerShape);
    }

    applyMultiJointEndpointTrimToFill(wall, innerShape);

    // Update stored corner state
    floorplannerStore.updateWallCornerStateFromShape(innerShape);

    // Get fresh corner state for trimmed endpoints calculation
    const freshWall = floorplannerStore.wallsMap.get(wallId) ?? wall;
    const persistedCorners = floorplannerStore.getCornerStateForApplication(freshWall);

    // Calculate trimmed endpoints if needed
    // In edge-based mode, this is typically undefined
    const trimmedEndpointsForWall = wall.lineForm === "arc" ? undefined : deriveTrimmedEndpointsFromState(wall, persistedCorners);

    // Use trimmed or original endpoints
    const outlineStart = trimmedEndpointsForWall?.start ?? wall.start;
    const outlineEnd = trimmedEndpointsForWall?.end ?? wall.end;

    // Add inner shape to output
    innerShapes.push(innerShape);

    // CREATE LEFT OUTLINE SHAPE
    const outerShape1: WallShapeType = {
        wall: { ...wall },
        paths: calculateLineVertices(
            outlineStart,
            outlineEnd,
            -(customWallWidth / 2),  // Left side offset (at edge of fill)
            customWallWeight,
            customWallWidth,
            wall.lineForm === "arc" ? "arcLeft" : "left",
            wall.controlPoint,
            undefined,
            arcInserts,
        ),
        lineSide: "left",
    };
    populateDefaultCorners(outerShape1);
    outerShapes1.push(outerShape1);

    // CREATE RIGHT OUTLINE SHAPE
    const outerShape2: WallShapeType = {
        wall: { ...wall },
        paths: calculateLineVertices(
            outlineStart,
            outlineEnd,
            customWallWidth / 2,  // Right side offset (at edge of fill)
            customWallWeight,
            customWallWidth,
            wall.lineForm === "arc" ? "arcRight" : "right",
            wall.controlPoint,
            undefined,
            arcInserts,
        ),
        lineSide: "right",
    };
    populateDefaultCorners(outerShape2);
    outerShapes2.push(outerShape2);
    // Check for connections at endpoints (for endcap determination)
    let hasStartConnectionForEndcap = false;
    let hasEndConnectionForEndcap = false;

    if (wall.edgeConnections) {
        // Check edge connections
        hasStartConnectionForEndcap = wall.edgeConnections.some(conn => {
            return (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'start') ||
                   (conn.targetWallId === wall.id && conn.targetEndpoint === 'start');
        });
        hasEndConnectionForEndcap = wall.edgeConnections.some(conn => {
            return (conn.sourceWallId === wall.id && conn.sourceEndpoint === 'end') ||
                   (conn.targetWallId === wall.id && conn.targetEndpoint === 'end');
        });
    } else {
        // Legacy mode: check wall.centerConnections
        const endPoints = wall.centerConnections?.filter(
            (c) => c.sourcePosition === WallConnectionEnd || c.sourcePosition === WallConnectionStart,
        ) || [];
        hasStartConnectionForEndcap = endPoints.some(c => c.sourcePosition === WallConnectionStart);
        hasEndConnectionForEndcap = endPoints.some(c => c.sourcePosition === WallConnectionEnd);
    }

    hasStartConnectionForEndcap = hasStartConnectionForEndcap || hasCollinearMiddleCoverAtEndpoint(wall, "start");
    hasEndConnectionForEndcap = hasEndConnectionForEndcap || hasCollinearMiddleCoverAtEndpoint(wall, "end");

    const startPartial = needsPartialEndcap(wall, WallConnectionStart);
    const endPartial = needsPartialEndcap(wall, WallConnectionEnd);

    // Case: no connections -> draw both caps once
    if (!hasStartConnectionForEndcap && !hasEndConnectionForEndcap) {
        const bothCapsShape: WallShapeType = {
            wall: { ...wall },
            paths: calculateLineVertices(
                outlineStart, outlineEnd, 0, customWallWeight, customWallWidth, "endCapBoth"
            ),
            lineSide: "endcap",
        };
        populateDefaultCorners(bothCapsShape);
        endCaps.push(bothCapsShape);
    } else {
        // Free ends get regular caps
        if (!hasStartConnectionForEndcap) {
            const startCapShape: WallShapeType = {
                wall: { ...wall },
                paths: calculateLineVertices(
                    outlineStart, outlineEnd, 0, customWallWeight, customWallWidth, "endCapStart"
                ),
                lineSide: "endcap",
            };
            populateDefaultCorners(startCapShape);
            endCaps.push(startCapShape);
        }
        if (!hasEndConnectionForEndcap) {
            const endCapShape: WallShapeType = {
                wall: { ...wall },
                paths: calculateLineVertices(
                    outlineStart, outlineEnd, 0, customWallWeight, customWallWidth, "endCapEnd"
                ),
                lineSide: "endcap",
            };
            populateDefaultCorners(endCapShape);
            endCaps.push(endCapShape);
        }
        // Connected ends may need partial bridges if the other wall is narrower
        // Use intersection logic to properly close the gap at any angle
        if (hasStartConnectionForEndcap && startPartial.needsPartialEndcap && startPartial.connectedWall) {
            const partialStartVertices = createPartialEndcapVertices(
                wall,
                startPartial.connectedWall,
                'start',
                customWallWidth,
                customWallWeight
            );
            if (partialStartVertices.length > 0) {
                const partialStartShape: WallShapeType = {
                    wall: { ...wall },
                    paths: partialStartVertices,
                    lineSide: "endcap",
                };
                populateDefaultCorners(partialStartShape);
                endCaps.push(partialStartShape);
            }
        }
        if (hasEndConnectionForEndcap && endPartial.needsPartialEndcap && endPartial.connectedWall) {
            const partialEndVertices = createPartialEndcapVertices(
                wall,
                endPartial.connectedWall,
                'end',
                customWallWidth,
                customWallWeight
            );
            if (partialEndVertices.length > 0) {
                const partialEndShape: WallShapeType = {
                    wall: { ...wall },
                    paths: partialEndVertices,
                    lineSide: "endcap",
                };
                populateDefaultCorners(partialEndShape);
                endCaps.push(partialEndShape);
            }
        }
      }
    } catch (error) {
      console.error('Error in composePaths:', error);
      throw error;
    } finally {
      // Always clear reconstruction flag after path composition is complete
      renderStore.setIsReconstructingShapes(false);
      if (renderStore.deferredUpdateDepth === 0) {
        renderStore.setSafeToRedraw(true);
      }
    }
}
